import { useEffect, useState } from 'react'

import { useVariantLensApiClient } from '../auth/hooks/useVariantLensApiClient'
import type { FacetCounts } from './variantQuery'

export type CaseFacets = {
  /** Total variant records in the whole case (not just the loaded page). */
  total: number
  counts: FacetCounts
}

export type CaseFacetsResult = {
  /** Whole-case counts from the precomputed sidecar, or null when unavailable. */
  facets: CaseFacets | null
  /**
   * Ordered INFO/TAG vocabulary for the resolved track, derived from the VCF
   * header by the API. Drives the sidebar TAG list/order so each VCF surfaces
   * its own categories. Empty when the track carries no TAG (e.g. a CNV VCF
   * without the field) or the request failed.
   */
  tagOrder: string[]
  /**
   * Human-readable definitions for the resolved track's TAG categories (SNV or
   * CNV vocabulary), for the legend. Empty when the API returned none; callers
   * fall back to their static SNV definitions.
   */
  tagDefinitions: Record<string, string>
  /**
   * The track these results belong to (the `track` arg that produced them), or
   * undefined while loading/empty or on the default track. Lets callers avoid
   * acting on a previous track's `tagOrder` during a track change, before the
   * refetch for the new track completes.
   */
  track?: string
  /**
   * True once a facets response has been applied for the current request (any
   * track, regardless of `available` or an empty vocabulary); false while
   * loading, empty, or after an error. Lets callers tell "vocabulary not ready
   * yet" apart from "this track legitimately has no TAG categories."
   */
  loaded: boolean
}

type FacetsResponse = {
  available: boolean
  total?: number
  tags?: Record<string, number>
  filters?: Record<string, number>
  chroms?: Record<string, number>
  tag_order?: string[]
  tag_definitions?: Record<string, string>
}

const EMPTY: CaseFacetsResult = { facets: null, tagOrder: [], tagDefinitions: {}, loaded: false }

/**
 * Fetch precomputed whole-case facet counts and the header-derived TAG
 * vocabulary from GET /cases/{caseId}/variants/facets for the selected track.
 *
 * `facets` is null when the endpoint reports no sidecar yet (available:false),
 * errors, or is still loading, so callers fall back to client-side, page-local
 * counts. `tagOrder`/`tagDefinitions` are returned independently of the sidecar
 * (they come from the VCF header), so the sidebar can show the real tag
 * vocabulary even before any counts exist. `track` selects a non-default track
 * (e.g. the CNV track); omit for the default so counts/vocabulary follow it.
 * `basePath` (GEN-5495) overrides the resource prefix so the order and
 * benchmark viewers reuse this loader against `/orders/{token}/variants/facets`
 * and `/benchmarks/{set}/{sample}/variants/facets`; it is a caller-built,
 * already-encoded path with no trailing slash, defaulting to the case path.
 */
export function useCaseFacets(caseId: string | undefined, track?: string, basePath?: string): CaseFacetsResult {
  const apiClient = useVariantLensApiClient()
  const [result, setResult] = useState<CaseFacetsResult>(EMPTY)

  useEffect(() => {
    if (!caseId) {
      setResult(EMPTY)
      return
    }
    const abort = new AbortController()
    setResult(EMPTY)
    const base = `${basePath ?? `/cases/${encodeURIComponent(caseId)}`}/variants/facets`
    const url = track ? `${base}?track=${encodeURIComponent(track)}` : base
    void (async () => {
      try {
        const res = await apiClient.get(url, undefined, true, abort.signal)
        if (!res.ok) return
        const body = (await res.json()) as FacetsResponse
        if (abort.signal.aborted) return
        const tagOrder = Array.isArray(body.tag_order)
          ? body.tag_order.filter((t): t is string => typeof t === 'string')
          : []
        const tagDefinitions =
          body.tag_definitions && typeof body.tag_definitions === 'object' ? body.tag_definitions : {}
        const facets: CaseFacets | null = body.available
          ? {
              total: body.total ?? 0,
              counts: { tags: body.tags ?? {}, filters: body.filters ?? {}, chroms: body.chroms ?? {} },
            }
          : null
        setResult({ facets, tagOrder, tagDefinitions, track, loaded: true })
      } catch {
        // Network/abort: stay empty; the sidebar uses page-local counts.
      }
    })()
    return () => abort.abort()
  }, [apiClient, caseId, track, basePath])

  return result
}
